import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import Image from 'next/image';

const ImageModal = ({ image, onClose }) => {
  const { themeMode } = usePlanningContext();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // Transform image prop to appropriate format if it's a string
  const imageObj = typeof image === 'string' ? { src: image } : image;
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    
    // Prevent scrolling on body when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);
  
  return (
    <AnimatePresence>
      {image && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 25 }}
            className={`relative max-w-5xl max-h-[90vh] overflow-hidden rounded-lg ${
              themeMode === 'dark' ? 'bg-gray-800 text-white' : 
              themeMode === 'light' ? 'bg-white text-gray-800' : 
              'bg-amber-50 text-amber-900'
            }`}
          >
            <div className="p-4 flex justify-between items-center border-b border-opacity-20">
              <h2 className="font-medium truncate pr-4">
                {imageObj.title || 'Planning Application Image'}
              </h2>
              <button
                onClick={onClose}
                className={`p-2 rounded-full ${
                  themeMode === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 
                  themeMode === 'light' ? 'hover:bg-gray-100 text-gray-600' : 
                  'hover:bg-amber-100 text-amber-800'
                }`}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="relative overflow-auto max-h-[70vh] flex items-center justify-center">
              {!imageLoaded && !error && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                </div>
              )}
              
              {error ? (
                <div className="p-8 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p>Failed to load image. The image may be unavailable or restricted.</p>
                </div>
              ) : (
                <div className="relative w-full h-full max-h-[inherit] min-h-[200px] min-w-[200px]">
                  <Image
                    src={imageObj.src}
                    alt={imageObj.alt || 'Planning application visual'}
                    layout="fill"
                    objectFit="contain"
                    className="p-4"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => {
                      setError(true);
                      setImageLoaded(true);
                    }}
                    style={{ display: imageLoaded ? 'block' : 'none' }}
                    unoptimized
                  />
                </div>
              )}
            </div>
            
            {imageObj.caption && (
              <div className="p-4 text-sm opacity-70">
                {imageObj.caption}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageModal;
